// Cloudinary upload helper with graceful backend fallback
// Preferred: client unsigned upload with env vars
// Fallback: POST to backend /api/upload/media with FormData

import ApiService from './api';

class CloudinaryService {
  static async uploadFile(file, folder = 'jobit/uploads') {
    if (!file) throw new Error('No file provided for upload');

    // Try client-side unsigned upload if env vars are present
    const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
    const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;
    if (cloudName && uploadPreset) {
      try {
        const mime = file.type || '';
        const isImage = mime.startsWith('image/');
        const isVideo = mime.startsWith('video/');
        const typePath = isImage ? 'image' : isVideo ? 'video' : 'raw';
        const url = `https://api.cloudinary.com/v1_1/${cloudName}/${typePath}/upload`;
        const data = new FormData();
        data.append('file', file);
        data.append('upload_preset', uploadPreset);
        data.append('folder', folder);

        const resp = await fetch(url, {
          method: 'POST',
          body: data,
        });
        if (!resp.ok) {
          const text = await resp.text();
          throw new Error(`Cloudinary upload failed: ${text}`);
        }
        const json = await resp.json();
        return {
          url: json.secure_url || json.url,
          publicId: json.public_id,
          originalFilename: json.original_filename,
          resourceType: json.resource_type,
        };
      } catch (e) {
        // Fall through to backend upload
        console.warn('Client Cloudinary upload failed, falling back to backend:', e?.message || e);
      }
    } else {
      console.warn('Cloudinary env config missing; using backend upload route.');
    }

    // Backend fallback(s): try common endpoints via ApiService
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    const category = folder.includes('profile') ? 'profile' : folder.includes('cv') ? 'document' : 'general';
    formData.append('category', category);

    const tryEndpoints = ['/upload/media', '/upload', '/files/upload', '/media/upload'];
    let lastError = null;
    for (const ep of tryEndpoints) {
      try {
        const res = await ApiService.request(ep, { method: 'POST', body: formData });
        const ok = res && (res.success !== false);
        const url = res?.fileUrl || res?.cloudinaryUrl || res?.url;
        if (ok && url) {
          return {
            url,
            publicId: res.cloudinaryPublicId || res.publicId,
            originalFilename: res.fileName || res.originalFilename,
            resourceType: res.resourceType || 'auto',
          };
        }
        // If response indicates failure, throw to trigger next endpoint
        throw new Error(res?.message || res?.error || 'Upload failed');
      } catch (err) {
        lastError = err;
        continue;
      }
    }
    throw new Error(`Backend upload failed: ${lastError?.message || 'No upload endpoint found'}`);
  }
}

export default CloudinaryService;